import { ReactNode } from "react";
import styled from "styled-components";
import {
  MedicineBoxOutlined,
  ExperimentOutlined,
  ReadOutlined,
  HeartOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  RocketOutlined,
  TeamOutlined,
  SmileOutlined,
} from "@ant-design/icons";

import { MainColor, SubColor } from "../../style/styleValue";
import SectionHeading from "./SectionHeading";
import useReveal, { revealStyle } from "./useReveal";
import { useMessages } from "../../i18n";

interface Pillar {
  icon: ReactNode;
  title: string;
  english: string;
  desc: string;
}

interface VisionItem {
  number: string;
  title: string;
  desc: string;
}

interface ValueItem {
  icon: ReactNode;
  title: string;
  desc: string;
}

const pillarsKo: Pillar[] = [
  {
    icon: <MedicineBoxOutlined />,
    title: "진단",
    english: "Diagnosis",
    desc: "진단이 어려운 희귀 증례를 다기관 전문가 자문과 NGS 기반 분자 진단으로 연결해 최종 확진의 기회를 넓힙니다.",
  },
  {
    icon: <ExperimentOutlined />,
    title: "연구",
    english: "Research",
    desc: "디지털 슬라이드, 임상 정보, 면역조직화학검사, 유전체 데이터를 한곳에 모아 희귀질환 연구의 토대를 만듭니다.",
  },
  {
    icon: <ReadOutlined />,
    title: "교육",
    english: "Education",
    desc: "검증된 증례와 주석이 달린 고해상도 슬라이드로 차세대 소아병리 전문가를 길러냅니다.",
  },
];

const visionItemsKo: VisionItem[] = [
  { number: "01", title: "통합 데이터 플랫폼", desc: "고해상도 디지털 슬라이드, 임상 정보, 면역조직화학검사, NGS 유전체 데이터를 하나의 플랫폼에 통합한 세계 최초의 소아 희귀질환 병리 아틀라스를 구축합니다." },
  { number: "02", title: "정밀 진단 중앙센터", desc: "NGS 기반 분자 진단을 통해 진단이 어려운 환아들에게 최종 확진의 기회를 제공하고, 전국 의료기관에 전문 자문 서비스를 제공합니다." },
  { number: "03", title: "차세대 전문인력 양성", desc: "디지털화된 전문 지식과 방대한 증례 데이터를 바탕으로 소아병리 분야의 차세대 리더를 육성하는 교육 플랫폼이 됩니다." },
  { number: "04", title: "글로벌 오픈 데이터베이스", desc: "전 세계 의료 전문가들이 자유롭게 접근하고 활용할 수 있는 오픈 데이터베이스로 글로벌 희귀질환 연구 발전에 기여합니다." },
];

const valueItemsKo: ValueItem[] = [
  { icon: <HeartOutlined />, title: "환자중심", desc: "희귀질환 환아와 가족에게 정확한 진단과 최적의 치료 정보를 제공하여 실질적인 의료 혜택을 만듭니다." },
  { icon: <SafetyCertificateOutlined />, title: "전문성", desc: "다기관 소아병리 전문가 그룹의 협력과 검증으로 세계 최고 수준의 데이터 품질을 유지합니다." },
  { icon: <GlobalOutlined />, title: "개방성", desc: "오픈 소스 정신을 바탕으로 전 세계 의료진과 데이터를 공유하며 글로벌 의학 발전에 기여합니다." },
  { icon: <RocketOutlined />, title: "혁신추구", desc: "AI와 빅데이터 기술을 접목하여 차세대 디지털 병리 진단 시스템의 토대를 구축합니다." },
  { icon: <TeamOutlined />, title: "인재양성", desc: "풍부한 디지털 콘텐츠와 교육 프로그램으로 소아병리 분야의 차세대 전문가를 육성합니다." },
  { icon: <SmileOutlined />, title: "사회공헌", desc: "희귀질환 진단 사각지대를 해소하고 의료 형평성을 높여 국민 건강 증진에 기여합니다." },
];

const pillarsEn: Pillar[] = [
  {
    icon: <MedicineBoxOutlined />,
    title: "Diagnosis",
    english: "",
    desc: "We connect hard-to-diagnose rare cases with multi-institutional expert consultation and NGS-based molecular diagnosis, widening the chance of a definitive diagnosis.",
  },
  {
    icon: <ExperimentOutlined />,
    title: "Research",
    english: "",
    desc: "We bring digital slides, clinical information, immunohistochemistry and genomic data together to build a foundation for rare disease research.",
  },
  {
    icon: <ReadOutlined />,
    title: "Education",
    english: "",
    desc: "We train the next generation of pediatric pathologists with validated cases and annotated high-resolution slides.",
  },
];

const visionItemsEn: VisionItem[] = [
  { number: "01", title: "Integrated Data Platform", desc: "We are building the world's first pediatric rare disease pathology atlas, integrating high-resolution digital slides, clinical information, immunohistochemistry and NGS genomic data into a single platform." },
  { number: "02", title: "Central Precision Diagnosis Center", desc: "Through NGS-based molecular diagnosis, we give children with hard-to-diagnose conditions a chance at a definitive diagnosis and provide expert consultation to medical institutions nationwide." },
  { number: "03", title: "Training the Next Generation", desc: "Built on digitized expertise and extensive case data, BePath serves as an educational platform that develops the next leaders in pediatric pathology." },
  { number: "04", title: "Global Open Database", desc: "As an open database that medical professionals worldwide can freely access and use, BePath contributes to the advancement of rare disease research around the world." },
];

const valueItemsEn: ValueItem[] = [
  { icon: <HeartOutlined />, title: "Patient-Centered", desc: "We provide accurate diagnoses and the best treatment information to children with rare diseases and their families, creating real medical benefit." },
  { icon: <SafetyCertificateOutlined />, title: "Expertise", desc: "Collaboration and validation by a multi-institutional group of pediatric pathologists keep our data quality at a world-class level." },
  { icon: <GlobalOutlined />, title: "Openness", desc: "In the spirit of open source, we share data with clinicians around the world and contribute to the advancement of global medicine." },
  { icon: <RocketOutlined />, title: "Innovation", desc: "By combining AI and big data technologies, we lay the groundwork for next-generation digital pathology diagnosis." },
  { icon: <TeamOutlined />, title: "Talent Development", desc: "Rich digital content and educational programs nurture the next generation of pediatric pathology experts." },
  { icon: <SmileOutlined />, title: "Social Contribution", desc: "We close gaps in rare disease diagnosis and improve healthcare equity, contributing to better public health." },
];

const ko = {
  pillars: pillarsKo,
  visionItems: visionItemsKo,
  valueItems: valueItemsKo,
  missionTitle: (
    <>
      고해상도 디지털 병리 통합 플랫폼으로{' '}
      <br />
      소아 희귀질환 환아들이{' '}
      <br />
      <em>정확한 진단</em>과 <em>최적의 치료</em>를{' '}
      <br />
      받을 수 있도록 합니다.
    </>
  ),
  missionDesc:
    "소아 희귀질환은 증례가 적어 경험 많은 전문가조차 진단에 오래 고민합니다. " +
    "BePath는 흩어져 있던 증례와 전문가의 지식을 하나의 디지털 플랫폼에 모아, " +
    "진단·연구·교육이 서로를 키우는 선순환을 만듭니다.",
  visionTitle: <>임상·병리·유전 정보가 통합된<br />세계 최초의 소아 희귀질환 병리 아틀라스</>,
  valuesTitle: "우리가 지키는 여섯 가지 가치",
  valuesDesc: "환자를 중심에 두고, 전문가의 검증과 열린 공유로 소아병리의 내일을 준비합니다.",
};

const en: typeof ko = {
  pillars: pillarsEn,
  visionItems: visionItemsEn,
  valueItems: valueItemsEn,
  missionTitle: (
    <>
      Through an integrated high-resolution{' '}
      <br />
      digital pathology platform, we help children{' '}
      <br />
      with rare diseases receive an <em>accurate diagnosis</em>{' '}
      <br />
      and the <em>best possible treatment</em>.
    </>
  ),
  missionDesc:
    "Rare pediatric diseases have so few cases that even experienced specialists can struggle to reach a diagnosis. " +
    "BePath gathers scattered cases and expert knowledge into a single digital platform, " +
    "creating a virtuous cycle in which diagnosis, research and education strengthen one another.",
  visionTitle: <>The world's first pediatric rare disease pathology atlas<br />integrating clinical, pathological and genetic information</>,
  valuesTitle: "Six values we uphold",
  valuesDesc: "Putting patients first, we prepare for the future of pediatric pathology through expert validation and open sharing.",
};

// Bepath 소개 > 미션과 비전 내용을 랜딩 흐름에 맞게 재구성한 섹션 (미션 · 비전 · 핵심가치)
const MissionSection = () => {
  const ref = useReveal<HTMLDivElement>();
  const t = useMessages({ ko, en });

  return (
    <Wrapper ref={ref} id="mission">
      <MissionBlock>
        <div className="container">
          <MissionGrid>
            <MissionText className="reveal">
              <span className="eyebrow">Our Mission</span>
              <h2>{t.missionTitle}</h2>
              <p>{t.missionDesc}</p>
            </MissionText>

            <PillarList>
              {t.pillars.map((pillar, index) => (
                <PillarCard
                  key={pillar.title}
                  className="reveal"
                  style={{ "--reveal-delay": `${0.12 * (index + 1)}s` } as React.CSSProperties}
                >
                  <div className="icon">{pillar.icon}</div>
                  <div className="body">
                    <div className="title-row">
                      <h3>{pillar.title}</h3>
                      {pillar.english && <span>{pillar.english}</span>}
                    </div>
                    <p>{pillar.desc}</p>
                  </div>
                </PillarCard>
              ))}
            </PillarList>
          </MissionGrid>
        </div>
      </MissionBlock>

      <VisionBlock>
        <div className="container">
          <SectionHeading
            tone="dark"
            eyebrow="Vision"
            title={t.visionTitle}
          />
          <VisionGrid>
            {t.visionItems.map((item, index) => (
              <VisionCard
                key={item.number}
                className="reveal"
                style={{ "--reveal-delay": `${0.1 * index}s` } as React.CSSProperties}
              >
                <span className="number">{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </VisionCard>
            ))}
          </VisionGrid>
        </div>
      </VisionBlock>

      <ValuesBlock>
        <div className="container">
          <SectionHeading
            eyebrow="Core Values"
            title={t.valuesTitle}
            description={t.valuesDesc}
          />
          <ValuesGrid>
            {t.valueItems.map((item, index) => (
              <ValueCard
                key={item.title}
                className="reveal"
                style={{ "--reveal-delay": `${0.08 * index}s` } as React.CSSProperties}
              >
                <div className="icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </ValueCard>
            ))}
          </ValuesGrid>
        </div>
      </ValuesBlock>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${revealStyle}

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
`;

const MissionBlock = styled.section`
  padding: 120px 0;
  background: #fff;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const MissionText = styled.div`
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${SubColor};
    margin-bottom: 24px;

    &::before {
      content: "";
      width: 24px;
      height: 2px;
      border-radius: 2px;
      background: currentColor;
    }
  }

  h2 {
    font-size: clamp(1.5rem, 2.6vw, 2.1rem);
    font-weight: 700;
    line-height: 1.5;
    letter-spacing: -0.03em;
    color: #111;
    margin: 0 0 28px;
    word-break: keep-all;

    em {
      font-style: normal;
      background: linear-gradient(90deg, ${MainColor}, ${SubColor});
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }

    @media (max-width: 576px) {
      br {
        display: none;
      }
    }
  }

  p {
    font-size: 1.05rem;
    line-height: 1.85;
    color: #5b6472;
    margin: 0;
    word-break: keep-all;
  }
`;

const PillarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;

  /* 세 축을 잇는 세로선 */
  &::before {
    content: "";
    position: absolute;
    left: 39px;
    top: 40px;
    bottom: 40px;
    width: 2px;
    background: linear-gradient(${SubColor}33, ${SubColor}, ${SubColor}33);
  }
`;

const PillarCard = styled.div`
  position: relative;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  padding: 24px;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(23, 55, 95, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &.is-visible:hover {
    transform: translateX(6px);
    border-color: ${SubColor}55;
    box-shadow: 0 12px 32px rgba(23, 55, 95, 0.12);
  }

  .icon {
    position: relative;
    z-index: 1;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    color: #fff;
    font-size: 16px;
    background: linear-gradient(135deg, ${MainColor}, ${SubColor});
    box-shadow: 0 0 0 6px #fff;
  }

  .title-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 6px;

    h3 {
      font-size: 1.2rem;
      font-weight: 700;
      color: #111;
      margin: 0;
    }

    span {
      font-size: 0.8rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: ${SubColor};
    }
  }

  p {
    font-size: 0.95rem;
    line-height: 1.7;
    color: #5b6472;
    margin: 0;
    word-break: keep-all;
  }
`;

const VisionBlock = styled.section`
  position: relative;
  padding: 120px 0;
  overflow: hidden;
  background:
    radial-gradient(circle at 15% 20%, rgba(48, 104, 164, 0.45), transparent 45%),
    radial-gradient(circle at 85% 80%, rgba(48, 104, 164, 0.3), transparent 40%),
    ${MainColor};

  /* 미세한 격자 패턴 */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
  }

  .container {
    position: relative;
  }

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const VisionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const VisionCard = styled.div`
  padding: 32px 28px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(6px);
  transition: background 0.25s ease, border-color 0.25s ease, transform 0.25s ease;

  &.is-visible:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(143, 182, 227, 0.5);
    transform: translateY(-6px);
  }

  .number {
    display: block;
    font-size: 2.4rem;
    font-weight: 300;
    line-height: 1;
    margin-bottom: 28px;
    color: transparent;
    -webkit-text-stroke: 1px #8fb6e3;
  }

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 14px;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.75;
    color: rgba(255, 255, 255, 0.72);
    margin: 0;
    word-break: keep-all;
  }
`;

const ValuesBlock = styled.section`
  padding: 120px 0;
  background: #f6f8fb;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  padding: 32px 28px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8edf3;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &.is-visible:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 30px rgba(23, 55, 95, 0.08);

    .icon {
      background: ${SubColor};
      color: #fff;
    }
  }

  .icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    font-size: 22px;
    color: ${SubColor};
    background: ${SubColor}14;
    margin-bottom: 20px;
    transition: background 0.25s ease, color 0.25s ease;
  }

  h3 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #111;
    margin: 0 0 10px;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.7;
    color: #5b6472;
    margin: 0;
    word-break: keep-all;
  }
`;

export default MissionSection;
